import moment from "moment";

export function generateLessonPlan({
  syllabusData,
  sessions,
  teacherPeriods,
  selectedClass,
  selectedSubject,
  selectedSection,
}) {
  if (
    !(teacherPeriods?.classes?.length > 0) ||
    !(teacherPeriods.subjects?.length > 0)
  ) {
    return { weeks: [], chapterPlan: [] };
  }

  const session = sessions.find((s) => s.status === "active");
  if (!session) return { weeks: [], chapterPlan: [] };

  const start = moment(session.start_date);
  const end = moment(session.end_date);
  const totalDays = end.diff(start, "days") + 1;

  const allDates = Array.from({ length: totalDays }, (_, i) => {
    const date = start.clone().add(i, "days");
    return { date, dayName: date.format("dddd") };
  });

  const periods = teacherPeriods?.periods || [];

  // 🔄 Filter syllabus based on class/subject
  const filteredSyllabi = Array.isArray(syllabusData)
    ? syllabusData.filter((item) => {
        const classMatch = selectedClass
          ? String(item.class_id) === selectedClass
          : true;
        const subjectMatch = selectedSubject
          ? String(item.subject_id) === selectedSubject
          : true;
        return classMatch && subjectMatch;
      })
    : [];

  const sectionPlans = [];

  for (const syllabus of filteredSyllabi) {
    const matchedClass = teacherPeriods.classes.find(
      (cls) => cls.class_id === syllabus.class_id
    );

    const sectionList =
      matchedClass?.sections?.filter((section) =>
        selectedSection ? section.section_id == selectedSection : true
      ) || [];

    const chapters = syllabus?.syllabus ? JSON.parse(syllabus.syllabus) : [];

    const subjectPeriods = periods.filter(
      (p) =>
        Number(p.subject_id) === Number(syllabus.subject_id) &&
        sectionList.some((section) => section.section_id === p.section_id)
    );

    const sectionWiseFlatPeriods = sectionList.map((section) => {
      const filteredPeriods = subjectPeriods.filter(
        (p) => p.section_id === section.section_id
      );

      const flatPeriods = [];

      allDates.forEach(({ date, dayName }) => {
        filteredPeriods
          .filter((p) => p.day === dayName)
          .forEach((p) =>
            flatPeriods.push({
              ...p,
              date: date.format("YYYY-MM-DD"),
            })
          );
      });

      const midDate = start
        .clone()
        .add(totalDays / 2, "days")
        .startOf("day");

      const term1Periods = flatPeriods.filter((p) =>
        moment(p.date).isSameOrBefore(midDate, "day")
      );
      const term2Periods = flatPeriods.filter((p) =>
        moment(p.date).isAfter(midDate, "day")
      );

      return {
        section_id: section.section_id,
        section_name: section.section_name,
        term1Periods,
        term2Periods,
      };
    });

    const term1Chapters = chapters.filter((ch) => ch.termId === 1);
    const term2Chapters = chapters.filter((ch) => ch.termId === 2);

    const allocatePeriods = (termChapters, termPeriodList, label) => {
      const tempPlan = [];
      let i = 0;
      termChapters.forEach((chapter) => {
        for (let j = 0; j < Number(chapter.numberofperiods); j++) {
          if (i >= termPeriodList.length) break;
          tempPlan.push({
            sno: tempPlan.length + 1,
            period: termPeriodList[i],
            className: termPeriodList[i].class_name,
            class_id: syllabus.class_id,
            subjectName: termPeriodList[i].subject_name,
            chapter: chapter.chapter,
            homework: "",
          });
          i++;
        }
      });

      // Remaining periods as Revision
      for (; i < termPeriodList.length; i++) {
        tempPlan.push({
          sno: tempPlan.length + 1,
          period: termPeriodList[i],
          className: termPeriodList[i].class_name,
          class_id: syllabus.class_id,
          subjectName: termPeriodList[i].subject_name,
          chapter: `Revision (${label})`,
          homework: "",
        });
      }

      return tempPlan;
    };

    sectionWiseFlatPeriods.forEach((section) => {
      const term1Plan = allocatePeriods(
        term1Chapters,
        section.term1Periods,
        "Term 1"
      );
      const term2Plan = allocatePeriods(
        term2Chapters,
        section.term2Periods,
        "Term 2"
      );

      sectionPlans.push({
        section_id: section.section_id,
        section_name: section.section_name,
        term1Plan,
        term2Plan,
      });
    });
  }

  // 📅 Final weekly grouping
  const fullPlan = sectionPlans.flatMap((s) => [
    ...s.term1Plan,
    ...s.term2Plan,
  ]);

  const groupedWeeks = {};
  fullPlan.forEach((item) => {
    const weekStart = moment(item.period.date)
      .startOf("isoWeek")
      .format("YYYY-MM-DD");
    if (!groupedWeeks[weekStart]) groupedWeeks[weekStart] = [];
    groupedWeeks[weekStart].push(item);
  });

  const sortedWeekStartDates = Object.keys(groupedWeeks)
    .map((d) => moment(d))
    .sort((a, b) => a.diff(b));

  const weeklyData = sortedWeekStartDates.map(
    (m) => groupedWeeks[m.format("YYYY-MM-DD")]
  );

  return { weeks: weeklyData, chapterPlan: fullPlan };
}
